/**
 * SheetJS is loaded on demand. A static import would put ~160kB of parser into
 * the main bundle for every visitor, when only the admin bulk page ever reads a
 * spreadsheet.
 */
const loadXLSX = () => import('xlsx');

export interface ParsedRecipient {
  name: string;
  email: string;
  phone: string;
  /** 1-based row number in the original sheet, for error messages. */
  row: number;
  valid: boolean;
  issue?: string;
}

export interface ParsedSheet {
  recipients: ParsedRecipient[];
  headers: string[];
  sheetName: string;
  /** Which sheet column was matched to each field, for the "detected columns" note. */
  matched: { name?: string; email?: string; phone?: string };
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Header aliases, checked against the header lowercased with spaces/punctuation stripped. */
const FIELD_ALIASES: Record<'name' | 'email' | 'phone', string[]> = {
  name: [
    'restaurantname',
    'restaurant',
    'name',
    'businessname',
    'business',
    'companyname',
    'company',
    'brand',
    'brandname',
    'outlet',
  ],
  email: ['email', 'emailaddress', 'mail', 'mailaddress', 'contactemail', 'workemail'],
  phone: [
    'phonenumber',
    'phone',
    'mobile',
    'mobilenumber',
    'telephone',
    'tel',
    'contact',
    'contactnumber',
    'whatsapp',
  ],
};

const normalizeHeader = (header: string): string =>
  String(header).toLowerCase().replace(/[^a-z0-9]/g, '');

/** Picks the sheet column whose header best matches a field. Earlier aliases win. */
const matchColumn = (
  headers: string[],
  field: 'name' | 'email' | 'phone'
): string | undefined => {
  const aliases = FIELD_ALIASES[field];
  const normalized = headers.map((h) => ({ raw: h, key: normalizeHeader(h) }));

  for (const alias of aliases) {
    const exact = normalized.find((h) => h.key === alias);
    if (exact) return exact.raw;
  }

  for (const alias of aliases) {
    const partial = normalized.find((h) => h.key.includes(alias));
    if (partial) return partial.raw;
  }

  return undefined;
};

/** Excel often reads phone columns as numbers — keep them readable, never scientific notation. */
const cellToString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toFixed(0) : String(value);
  }
  return String(value).trim();
};

/**
 * Reads the first sheet of an .xlsx / .xls / .csv file and maps its rows to
 * recipients. Rows keep their original position so problems can be pointed at
 * a specific line in the user's file.
 */
export const parseRecipientSheet = async (file: File): Promise<ParsedSheet> => {
  const XLSX = await loadXLSX();
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('That file has no sheets in it.');
  }

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: '',
    raw: true,
  });

  if (rows.length === 0) {
    throw new Error('The first sheet is empty — no rows to import.');
  }

  const headers = Object.keys(rows[0]);
  const matched = {
    name: matchColumn(headers, 'name'),
    email: matchColumn(headers, 'email'),
    phone: matchColumn(headers, 'phone'),
  };

  if (!matched.email) {
    throw new Error(
      `No email column found. Columns in the file: ${headers.join(', ')}. ` +
        'Rename one column to "email".'
    );
  }

  const seen = new Set<string>();

  const recipients: ParsedRecipient[] = rows.map((row, index) => {
    const email = cellToString(matched.email ? row[matched.email] : '').toLowerCase();
    const name = cellToString(matched.name ? row[matched.name] : '');
    const phone = cellToString(matched.phone ? row[matched.phone] : '');

    // +2 because row 1 is the header and the index is 0-based.
    const base = { name, email, phone, row: index + 2 };

    if (!email) return { ...base, valid: false, issue: 'Missing email' };
    if (!EMAIL_REGEX.test(email)) return { ...base, valid: false, issue: 'Invalid email' };
    if (seen.has(email)) return { ...base, valid: false, issue: 'Duplicate email' };

    seen.add(email);
    return { ...base, valid: true };
  });

  return { recipients, headers, sheetName, matched };
};

/** Fills {{name}} / {{email}} / {{phone}} in a template — mirrors the server. */
export const renderTemplate = (
  template: string,
  recipient: { name?: string; email?: string; phone?: string }
): string => {
  const values: Record<string, string> = {
    name: recipient.name || '',
    restaurantname: recipient.name || '',
    recipient: recipient.name || '',
    email: recipient.email || '',
    phone: recipient.phone || '',
    phonenumber: recipient.phone || '',
  };

  return template.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (match, key: string) => {
    const value = values[key.toLowerCase()];
    return value === undefined ? match : value;
  });
};

/** Builds a starter .xlsx with the expected headers so staff can fill it in. */
export const downloadTemplateSheet = async (): Promise<void> => {
  const XLSX = await loadXLSX();
  const sheet = XLSX.utils.aoa_to_sheet([
    ['restaurantName', 'phoneNumber', 'email'],
    ['The Place', '+234 801 234 5678', 'hello@theplace.com'],
    ['Cilantro Restaurant', '+234 802 345 6789', 'info@cilantro.com'],
  ]);
  sheet['!cols'] = [{ wch: 28 }, { wch: 22 }, { wch: 30 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Recipients');
  XLSX.writeFile(workbook, 'nexasolutra-bulk-recipients.xlsx');
};
